import { sendRequest } from './api';

export interface IMindmapObject {
    position: { x: number; y: number };
    width: number;
    height: number;
    angle: number;
    text: string;
    style: string;
    folderId?: string;
    folderName?: string;
    noteId?: string;
    taskId?: string;
    taskChecked?: boolean;
    fillColor?: string;
    textColor?: string;
    borderColor?: string;
    borderWidth?: number;
    mediaUrl?: string;
    mediaType?: 'image' | 'video' | 'audio';
    pathData?: string;
    strokeColor?: string;
    strokeWidth?: number;
    drawingOffset?: { x: number; y: number };
}

export type ArrowStyle = 'thin' | 'thick' | 'hollow' | 'straight-thin' | 'straight-thick' | 'straight-hollow' | 'manual-thin' | 'manual-thick' | 'manual-hollow' | 'block';
export type SnapPointId = 'top' | 'bottom' | 'left' | 'right' | 'tl' | 'tr' | 'bl' | 'br';

export interface IMindmapArrow {
    id: string;
    style: ArrowStyle;
    fromObjectIndex: number;
    toObjectIndex: number;
    fromSnapPoint: SnapPointId;
    toSnapPoint: SnapPointId;
    manualPath?: { x: number; y: number }[];
    text?: string;
}

export interface IMindmapData {
    objects: IMindmapObject[];
    arrows?: IMindmapArrow[];
    backgroundColor?: string;
    showDots?: boolean;
    backgroundType?: 'color' | 'cork';
}

export interface IMindmap {
    id: string;
    client_id: string;
    name: string;
    json?: IMindmapData;
}

export const createMindmap = async (clientId: string, name: string, json: IMindmapData): Promise<IMindmap | null> => {
    const response = await sendRequest('mindmap', 'POST', {
        client_id: clientId,
        name: name,
        json: JSON.stringify(json)
    }, '');
    
    if (response && response.ok) {
        const data = await response.json();
        return {
            id: data.created_id,
            client_id: clientId,
            name: name,
            json: json
        };
    }
    return null;
};

export const getMindmaps = async (clientId: string): Promise<IMindmap[]> => {
    const response = await sendRequest(`mindmap/${clientId}`, 'GET', null, '');
    
    if (response && response.ok) {
        const data = await response.json();
        if (!Array.isArray(data)) return [];
        
        return data.map((item: any) => {
            const id = Object.keys(item)[0];
            const content = item[id];
            let parsedJson: IMindmapData = { objects: [] };
            
            if (content.json && typeof content.json === 'string') {
                try {
                    parsedJson = JSON.parse(content.json);
                } catch (e) {
                    console.error('Failed to parse mindmap json:', e);
                }
            } else if (content.json) {
                parsedJson = content.json;
            }
            
            return {
                id,
                client_id: content.client_id,
                name: content.name,
                json: parsedJson
            };
        });
    }
    return [];
};

export const getMindmap = async (mindmapId: string): Promise<IMindmap | null> => {
    const response = await sendRequest(`mindmap/single/${mindmapId}`, 'GET', null, '');
    
    if (response && response.ok) {
        const data = await response.json();
        
        // Handle {id: {data}} format
        const keys = Object.keys(data);
        let content = data;
        let id = mindmapId;
        
        if (keys.length === 1 && typeof data[keys[0]] === 'object' && data[keys[0]].client_id) {
            id = keys[0];
            content = data[id];
        }
        
        let parsedJson: IMindmapData = { objects: [] };
        if (content.json && typeof content.json === 'string') {
            try {
                parsedJson = JSON.parse(content.json);
            } catch (e) {
                console.error('Failed to parse mindmap json:', e);
            }
        } else if (content.json) {
            parsedJson = content.json;
        }
        
        return {
            id,
            client_id: content.client_id,
            name: content.name,
            json: parsedJson
        };
    }
    return null;
};

export const updateMindmap = async (mindmapId: string, name: string, json: IMindmapData): Promise<boolean> => {
    const response = await sendRequest(`mindmap/single/${mindmapId}`, 'PUT', {
        name: name,
        json: JSON.stringify(json)
    }, '');
    
    return !!(response && response.ok);
};

export const deleteMindmap = async (mindmapId: string): Promise<boolean> => {
    const response = await sendRequest(`mindmap/single/${mindmapId}`, 'DELETE', null, '');
    
    return !!(response && response.ok);
};
